import { useCallback, useEffect, useRef, useState } from 'react'
import { getAiCompletions } from '../../../../api/commonApi'
import { useUserStore } from '../../../stores/useUserStore'
import type {
  NarrativeOcgValidationAction,
  NarrativeOcgValidationArgs,
  NarrativeOcgValidationHint,
  UseNarrativeOcgValidationOptions,
  UseNarrativeOcgValidationResult,
} from '../types'
import { isNarrativeOcgFeatureEnabled } from '../utils/isNarrativeOcgFeatureEnabled'
import { hasOcgValidationFindings } from '../utils/issueDetails'
import {
  normalizeTimesheetValidationResponse,
  normalizeTimesheetValidationResponses,
} from '../utils/normalizeTimesheetValidation'
import { validateTimesheetNarrative } from '../utils/validateTimesheets'

const emptyValidationHint: NarrativeOcgValidationHint = {
  visible: false,
  keyword: '',
  reason: '',
}

export const useNarrativeOcgValidation = ({
  description,
  clientId,
  timekeeperId,
  isBillable = true,
  initialValidationHint,
  getSignaturePayload,
  buildValidationItem,
  onApplyNarrative,
  getValidationNarrative,
  resetDeps = [],
  validateOnContextChange = false,
}: UseNarrativeOcgValidationOptions): UseNarrativeOcgValidationResult => {
  const selfUser = useUserStore(state => state.selfUser)
  const [validationHint, setValidationHint] =
    useState<NarrativeOcgValidationHint>(
      () => initialValidationHint ?? emptyValidationHint
    )
  const [actionLoading, setActionLoading] =
    useState<NarrativeOcgValidationAction | null>(null)
  const [isFieldValidationDirty, setIsFieldValidationDirty] = useState(false)
  const [prevAiSuggests, setPrevAiSuggests] = useState<string[]>([])

  const acceptedNarrativeSignaturesRef = useRef<Set<string>>(new Set())
  const latestNarrativeSignatureRef = useRef<string | null>(null)
  const validationRequestIdRef = useRef(0)
  const initialResetHandledRef = useRef(false)
  // Set once this editor runs or resolves its own validation, or its billing
  // context changes; a late initial hint must not override that work.
  const ownValidationRef = useRef(false)

  const getSignature = useCallback(
    (nextDescription = description, ...args: NarrativeOcgValidationArgs) =>
      JSON.stringify(getSignaturePayload(nextDescription, ...args)),
    [description, getSignaturePayload]
  )

  const resetAcceptedSignature = useCallback(() => {
    acceptedNarrativeSignaturesRef.current.clear()
  }, [])

  const clearValidationHint = useCallback(() => {
    validationRequestIdRef.current += 1
    setActionLoading(null)
    setPrevAiSuggests([])
    setValidationHint(emptyValidationHint)
  }, [])

  const clearFieldValidation = useCallback(
    (field: 'duration' | 'activity_code' | 'task_code') => {
      const category =
        field === 'duration'
          ? 'time'
          : field === 'activity_code'
            ? 'activity'
            : 'coding'

      ownValidationRef.current = true
      setIsFieldValidationDirty(true)
      setValidationHint(current => {
        const findings = (current.findings ?? []).filter(
          finding => finding.violation_category?.toLowerCase() !== category
        )
        if (findings.length === (current.findings ?? []).length) return current
        if (!findings.length) return emptyValidationHint

        const errorCount = findings.filter(
          finding => finding.severity === 'error'
        ).length
        const warningCount = findings.filter(
          finding => finding.severity === 'warning'
        ).length
        const infoCount = findings.filter(
          finding => finding.severity === 'info'
        ).length

        return {
          ...current,
          findings,
          errorCount,
          warningCount,
          infoCount,
          totalCount: findings.length,
          severity: errorCount
            ? 'error'
            : warningCount
              ? 'warning'
              : infoCount
                ? 'info'
                : undefined,
        }
      })
    },
    []
  )

  const handleNarrativeChange = useCallback(
    (nextDescription?: string | null) => {
      const nextSignature = getSignature(nextDescription)
      if (latestNarrativeSignatureRef.current === nextSignature) return

      // MIRA-4349: A review applies only while the narrative stays unchanged. Restoring
      // earlier wording after an edit must run the rules again.
      resetAcceptedSignature()
      latestNarrativeSignatureRef.current = nextSignature
      clearValidationHint()
    },
    [clearValidationHint, getSignature, resetAcceptedSignature]
  )

  const handleAccept = useCallback(
    (suggestedNarrative: string) => {
      ownValidationRef.current = true
      validationRequestIdRef.current += 1
      setActionLoading('accept')
      const acceptedSignature = getSignature(suggestedNarrative)
      // Skip duplicate validation until the narrative is edited again.
      acceptedNarrativeSignaturesRef.current.add(acceptedSignature)
      latestNarrativeSignatureRef.current = acceptedSignature
      onApplyNarrative(suggestedNarrative)
      clearValidationHint()
    },
    [clearValidationHint, getSignature, onApplyNarrative]
  )

  const handleDiscard = useCallback(
    (
      currentNarrative: string | null | undefined = description,
      ...args: NarrativeOcgValidationArgs
    ) => {
      ownValidationRef.current = true
      const narrative = currentNarrative ?? description ?? ''

      if (narrative.trim()) {
        const discardedSignature = getSignature(narrative, ...args)
        acceptedNarrativeSignaturesRef.current.add(discardedSignature)
        latestNarrativeSignatureRef.current = discardedSignature
      }

      clearValidationHint()
    },
    [clearValidationHint, description, getSignature]
  )

  const handleRevert = () => {
    setActionLoading('revert')
    const prevSuggests = [...prevAiSuggests]
    const prevSuggest = prevSuggests.pop()
    if (typeof prevSuggest !== 'string') {
      setPrevAiSuggests(prevSuggests)
      setActionLoading(null)
      return
    }
    setPrevAiSuggests(prevSuggests)
    setValidationHint(current => ({
      ...current,
      visible: true,
      isLoading: false,
      suggestedNarrative: prevSuggest,
    }))
    setActionLoading(null)
  }

  const handleRewrite = async () => {
    const currentNarrative = description || ''
    const suggestedRewriteNarrative =
      typeof validationHint.suggestedNarrative === 'string' &&
      validationHint.suggestedNarrative.trim()
        ? validationHint.suggestedNarrative
        : ''
    const rewriteKeyword = suggestedRewriteNarrative || currentNarrative
    if (!rewriteKeyword.trim()) return

    try {
      setActionLoading('rewrite')
      const aiResult = await getAiCompletions({
        keyword: rewriteKeyword,
        ...(clientId ? { client_id: clientId } : {}),
        ...(timekeeperId ? { tkpr_id: timekeeperId } : {}),
      })
      const suggestedNarrative = aiResult?.data
        ? aiResult.data.replaceAll('"', '')
        : ''

      if (!suggestedNarrative) return

      const previousSuggestedNarrative = validationHint.suggestedNarrative
      if (typeof previousSuggestedNarrative === 'string') {
        setPrevAiSuggests(pre => [...pre, previousSuggestedNarrative])
      }
      setValidationHint(current => ({
        ...current,
        source: 'ai',
        visible: true,
        isLoading: false,
        originalNarrative: current.originalNarrative || currentNarrative,
        suggestedNarrative,
      }))
    } catch (error) {
      console.error('Rewrite narrative failed:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleAutoNarrativeSuggestion = useCallback(
    (suggestedNarrative: string) => {
      if (!suggestedNarrative.trim()) return

      const previousSuggestedNarrative = validationHint.suggestedNarrative
      if (typeof previousSuggestedNarrative === 'string') {
        setPrevAiSuggests(pre => [...pre, previousSuggestedNarrative])
      }

      setActionLoading(null)
      setValidationHint(current => ({
        ...current,
        source: 'ai',
        visible: true,
        isLoading: false,
        originalNarrative: current.originalNarrative || description || '',
        suggestedNarrative,
      }))
    },
    [description, validationHint.suggestedNarrative]
  )

  const validate = useCallback(
    async (scope: boolean | 'context', ...args: NarrativeOcgValidationArgs) => {
      const narrativeOnly = scope === true
      const contextValidation = scope === 'context'
      ownValidationRef.current = true
      const narrative = getValidationNarrative?.(...args) ?? description ?? ''

      if (!narrative.trim()) {
        clearValidationHint()
        return true
      }

      if (!isBillable) {
        clearValidationHint()
        return true
      }

      if (!isNarrativeOcgFeatureEnabled()) {
        clearValidationHint()
        return true
      }

      if (
        narrativeOnly &&
        acceptedNarrativeSignaturesRef.current.has(
          getSignature(narrative, ...args)
        )
      ) {
        clearValidationHint()
        return true
      }

      setPrevAiSuggests([])
      const requestId = ++validationRequestIdRef.current
      const requestSignature = getSignature(narrative, ...args)
      latestNarrativeSignatureRef.current = requestSignature
      setValidationHint({
        visible: false,
        // Field validation skips the narrative check, so its in-flight
        // state must not cover the narrative editor with a loading overlay.
        isLoading: narrativeOnly || contextValidation,
        keyword: '',
        reason: '',
      })

      try {
        const validationItem = buildValidationItem(...args)
        const item = {
          ...validationItem,
          tkpr_id: timekeeperId ?? selfUser?.id ?? '',
        }
        const responses = await Promise.all(
          (contextValidation ? [true, false] : [narrativeOnly]).map(
            narrativeScope =>
              validateTimesheetNarrative(item, narrative, narrativeScope)
          )
        )

        if (requestId !== validationRequestIdRef.current) {
          // MIRA-4349: Never publish a result for an older narrative or billing context.
          // A superseded save still fails closed.
          return narrativeOnly
        }

        if (
          narrativeOnly &&
          acceptedNarrativeSignaturesRef.current.has(requestSignature)
        ) {
          clearValidationHint()
          return true
        }

        const hint = {
          ...(contextValidation
            ? normalizeTimesheetValidationResponses(responses, narrative)
            : normalizeTimesheetValidationResponse(responses[0], narrative)),
          validationScope: contextValidation
            ? 'context'
            : narrativeOnly
              ? 'narrative'
              : 'fields',
        } as NarrativeOcgValidationHint

        if (!narrativeOnly) setIsFieldValidationDirty(false)

        if (hasOcgValidationFindings(hint)) {
          setValidationHint(hint)
          return false
        }

        if (narrativeOnly) {
          acceptedNarrativeSignaturesRef.current.add(requestSignature)
        }
        clearValidationHint()
        return true
      } catch (error) {
        if (requestId !== validationRequestIdRef.current) {
          // A stale field-validation request still fails closed. Do not overwrite a
          // newer validation result with a generic transport error.
          return narrativeOnly
        }

        console.error('Validate timesheet narrative failed:', error)
        setValidationHint({
          visible: true,
          severity: 'error',
          ...(contextValidation
            ? {
                findings: [
                  {
                    severity: 'error' as const,
                    message:
                      'OCG validation failed. Please try again before saving.',
                  },
                ],
              }
            : {}),
          keyword: '',
          reason:
            'Narrative validation failed. Please try again before saving.',
          message:
            'Narrative validation failed. Please try again before saving.',
        })
        return false
      }
    },
    [
      buildValidationItem,
      clearValidationHint,
      description,
      getSignature,
      getValidationNarrative,
      isBillable,
      timekeeperId,
      selfUser?.id,
    ]
  )

  const validateBeforeSubmit = useCallback(
    (...args: NarrativeOcgValidationArgs) => validate(true, ...args),
    [validate]
  )

  const validateBeforeSave = useCallback(
    (...args: NarrativeOcgValidationArgs) => validate(false, ...args),
    [validate]
  )

  useEffect(() => {
    const contextChanged = initialResetHandledRef.current
    resetAcceptedSignature()
    if (!initialResetHandledRef.current) {
      initialResetHandledRef.current = true
      if (initialValidationHint?.visible) return
    } else {
      ownValidationRef.current = true
    }
    clearValidationHint()
    // MIRA-4349: Recheck both rule scopes for every billing-context change,
    // including changes made while an earlier validation is still pending.
    if (contextChanged && validateOnContextChange) {
      void validate('context')
    }
    // resetDeps is supplied by each form to describe the billing context that
    // invalidates a previously accepted OCG suggestion.
  }, resetDeps)

  // MIRA-4284: Draft-group OCG validation can finish after the editor opens.
  // Show that late result unless this editor already did its own validation
  // or the narrative changed since the draft was validated.
  // Suggestions and rule details can change while the findings stay the same.
  const initialHintKey = initialValidationHint?.visible
    ? JSON.stringify(initialValidationHint)
    : ''
  useEffect(() => {
    if (!initialHintKey || !initialValidationHint) return
    if (ownValidationRef.current) return
    if ((initialValidationHint.originalNarrative ?? '') !== (description ?? ''))
      return
    setValidationHint(current =>
      current.isLoading ? current : initialValidationHint
    )
  }, [initialHintKey])

  return {
    validationHint,
    actionLoading,
    isFieldValidationDirty,
    clearValidationHint,
    clearFieldValidation,
    handleNarrativeChange,
    resetAcceptedSignature,
    handleAccept,
    handleDiscard,
    handleRevert: prevAiSuggests.length > 0 ? handleRevert : undefined,
    handleRewrite,
    handleAutoNarrativeSuggestion,
    validateBeforeSubmit,
    validateBeforeSave,
  }
}
